import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { OnChanges, SimpleChanges } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { UploadService } from '../../services/upload.service';
import EXIF from 'exif-js';
import { heicTo, isHeic } from "heic-to"
import { imageUpload, ImageUploadVariables } from '@dataconnect/account';
import { ImagePresignReturn, ImageUploadPublic, ImageUploadType } from '../../../../globalTypes/image.types';
// TODO: Reorder images after upload
export interface UploadedFile {
  url: string;
  filename: string;
  contentType: string;
  sizeInBytes: number;
  width: number;
  height: number;
  position?: number;
  key?: string;
}


@Component({
  selector: 'msh-upload-image',
  standalone: true,
  imports: [
    FormsModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule
],
  template: `
    <div class="space-y-3 mb-7 mt-4 flex flex-wrap items-center gap-7 justify-center">
      <!-- Drop zone {{maxFiles}}:{{files.length}}:{{existingImages.length}}-->
         
       @if(maxFiles >  files.length + existingImages.length){
        <div [class]="dropZoneOnTopAlone ? 'flex justify-center w-full' : 'mt-2'">
          <div
            class="border-4 m-auto border-copy/15 cursor-pointer flex items-center justify-center border-dashed rounded w-48 h-48 relative"
            (click)="fileInput.click()"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
            [class.opacity-70]="isDragging"
          >
            <div class="text-center p-2">
              <mat-icon class="text-3xl text-copy">cloud_upload</mat-icon>
              <div class="text-xs text-copy mt-1">Drop or click to upload</div>
              <div class="text-xs text-copy mt-1">gif, jpg, png • max 2MB</div>
            </div>

            <input
              #fileInput
              type="file"
              class="hidden"
              [attr.multiple]="maxFiles > 1 ? '' : null"
              accept="image/gif,image/jpeg,image/png"
              (change)="onFilesSelected($event)"
            />
          </div>
        </div>
       }

      <!-- Thumbnails + controls -->
        @for( f of files; track f; let i = $index){
          <div  class=" w-48 h-48 relative border rounded p-2 flex flex-col items-center justify-center bg-back/5" >
            <div class="w-full h-24 bg-copy/10 flex items-center justify-center overflow-hidden rounded">
              @if(f.preview){
                <img
                  [src]="f.preview"
                  [class]="'max-h-full max-w-full  object-contain' + (f.uploadErrors ? ' opacity-35 ' : '') + (uploadingFiles.includes(f.filename) && !f.uploadErrors ? ' animate-custom-pulse-opacity ' : '')"
                  alt="{{ f.filename }}"
                />
              } @else {
                <div class="text-xs text-copy">Preview unavailable</div>
              }
            </div>

            <div class="w-full mt-2 text-xs justify-center text-copy/75 text-center truncate">{{ f.filename }}</div>
            @if(f.uploadErrors){
              <div class="text-sm mt-2 text-red-600">{{f.uploadErrors}}</div>
            }
            @if(f.uploading){
              <mat-progress-bar
                [value]="f.progress"
                mode="determinate"
                class="w-full mt-2"
              ></mat-progress-bar>
            }

            <mat-icon (click)="removeFiles([i])" class="text-sm bg-copy/15 hover:opacity-100 opacity-50 rounded-r cursor-pointer pl-1 pt-0.5 absolute top-0 -right-6" title="Delete">delete</mat-icon>
            @if(f.error){
              <div class="text-xs mt-1 text-orange-300">{{ f.error }}</div>
            }
          </div>
        }
        @for(existingImage of existingImages; track existingImage; let i = $index){
          <div  class=" w-48 h-48 relative border rounded p-2 flex flex-col items-center justify-center bg-back/5" >
            <div class="w-full h-24 bg-copy/10 flex items-center justify-center overflow-hidden rounded">
              <img
                [src]="existingImage.image.url"
                class="max-h-full max-w-full object-contain"
              />
            </div>
            <mat-icon (click)="removeImage(existingImage.image.id)" class="text-sm bg-navy-50 hover:opacity-100 opacity-50 rounded-r cursor-pointer pl-1 pt-0.5  absolute top-0 -right-6" title="Delete">delete</mat-icon>
          </div>
        }

      <!-- Actions
      <div class="flex gap-2 items-center">
        <button mat-flat-button class="bg-navy-700 text-white px-4 py-2 rounded" (click)="triggerUpload()" [disabled]="uploadingAny">
          Upload
        </button>

        <button mat-stroked-button class="px-3 py-1" (click)="finalizeAll()" [disabled]="!hasUploadedTemp">
          Finalize
        </button>
      </div> -->
        @if(uploadingAny){
          <div class="ml-3 text-sm text-navy-300">Uploading...</div>
        }
    </div>
  `,
  styles: [`
    :host { display:block; }
    .border-navy { border-color: rgba(2,6,23,0.12); }
    .text-navy-700 { color: rgba(2,6,23,0.85); }
    .bg-navy-700 { background-color: #0b2545; }
    .text-orange-300 { color: #fbbf24; }
  `],
})
export class UploadImageComponent implements OnInit, OnChanges {
  @Input() type: ImageUploadType = 'listing';
  @Input() locationId!: string
  @Input() id!: string | number;
  @Input() maxFiles = 10;
  @Input() dropZoneOnTopAlone = true;
  @Input() maxFileSizeBytes = 2 * 1024 * 1024; // 2 MB
  @Input() existingImages: { image: ImageUploadPublic; sortOrder: number }[] = []
  @Output() onImageLoading = new EventEmitter<string[]>();
  @Output() onImageObjectsUpdated = new EventEmitter<{image: ImageUploadPublic; sortOrder: number}[]>();
  // @Output() error = new EventEmitter<string>();

  files: Array<any> = []; // local state for previews and upload progress
  imageObjects: { image: ImageUploadPublic; sortOrder: number }[] = []
  uploadingFiles: string[] = [];
  isDragging = false;
  uploadingAny = false;
  hasUploadedTemp = false; // tracks if any temp upload succeeded
  // functions = getFunctions();

  constructor(
    private snack: MatSnackBar,
    private uploadService: UploadService,
  ) {}

  async ngOnInit(): Promise<void> { }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingImages']) {
      this.imageObjects = [...this.existingImages];
      this.files = [];
    }
  }


  // Drag handlers
  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.isDragging = true;
  }
  onDragLeave(ev: DragEvent) {
    this.isDragging = false;
  }
  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.isDragging = false;
    const dt = ev.dataTransfer;
    if (dt?.files?.length) {
      this.handleFileList(dt.files);
    }
  }

  // onFilesSelected(e: Event) {
  //   const el = e.target as HTMLInputElement;
  //   if (el.files) {
  //     this.handleFileList(el.files);
  //     el.value = ''; // reset input
  //   }
  // }

  handleFileList(fileList: FileList) {
    const filesArr = Array.from(fileList);
    for (const file of filesArr) {
      if (!this.isValidFile(file)) continue;
      if (this.maxFiles === 1) {
        // replace semantics
        const allFileIndexes = [...Array(10).keys()]
        this.removeFiles(allFileIndexes); // remove previews and delete any temp on server
      } else {
        if (this.files.length >= Math.min(this.maxFiles, 10)) {
          this.snack.open(`Max files (${this.maxFiles}) reached`, 'Close', { duration: 2000 });
          break;
        }
      }
      const local = {
        file,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        progress: 0,
        uploading: false,
        error: null,
        preview: null,
        uploadedMeta: null, // will hold UploadedFile
      };
      this.files.push(local);
      this.generatePreview(local);
    }
  }

  isValidFile(file: File) {
    const allowed = ['image/gif', 'image/png', 'image/jpeg'];
    if (!allowed.includes(file.type)) {
      this.snack.open('Invalid file type', 'Close', { duration: 2000 });
      return false;
    }
    if (file.size > this.maxFileSizeBytes) {
      this.snack.open('File too large (max 2MB)', 'Close', { duration: 2000 });
      return false;
    }
    return true;
  }

  generatePreview(local: any) {
    const fr = new FileReader();
    fr.onload = () => {
      local.preview = fr.result as string;
    };
    fr.readAsDataURL(local.file);
    this.triggerUpload();
  }

  onFilesSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files) {
      Array.from(input.files).forEach((file) => {
        this.files.push({ file, filename: file.name, preview: URL.createObjectURL(file), uploading: false, progress: 0 });
      });
    }
    this.triggerUpload();
  }

  updateLoading(fileNames: string[], remove = false){
    if(remove){
      this.uploadingFiles = this.uploadingFiles.filter(fileName=>!fileNames.includes(fileName))
    }else{
      this.uploadingFiles.push(...fileNames);
    }
    this.onImageLoading.emit(this.uploadingFiles)
  }

  async triggerUpload(skipConvertToWebp = false) {
    let idx = this.existingImages.length;
    for (const f of this.files) {
      if (f.uploadedMeta) continue; // skip already uploaded
      if(!skipConvertToWebp) {
        const isHeicFile = await isHeic(f.file);
        const fileToConvert = await (isHeicFile ? convertHeicToJpeg(f.file) : f.file);
        const convertedFile = await convertToWebp(fileToConvert, 0.8);
        f.file = convertedFile;
        const newName = f.filename.replace(/\.\w+$/, '.webp');
        f.filename = newName;
      }
      this.updateLoading([f.filename])
      f.uploadErrors = undefined;
      try {
        const presign: ImagePresignReturn = await this.uploadService.presignUpload({
          locationId: this.locationId,
          type: this.type,
          id: this.id.toString(),
          filename: f.filename,
          contentType: f.file.type,
        });

        // PUT to R2
        await fetch(presign.uploadUrl, { method: 'PUT', body: f.file, headers: { 'Content-Type': f.file.type } });

        const dims = await this.getImageDimensions(f.file);
        console.log({dims})
        const vars: ImageUploadVariables = {
          url: presign.publicUrl,
          filename: f.filename,
          contentType: f.file.type,
          sizeInBytes: f.file.size,
          width: dims.width,
          height: dims.height,
          orientation: dims.orientation,
          key: presign.key,
        };
        f.uploadedMeta = vars;

        console.log({vars});
        // await this.uploadService.registerUpload({ key: presign.key, metadata: f.uploadedMeta });
        const uploadResult = await imageUpload(vars);
        
        const id = uploadResult.data.image_insert.id
        const image: ImageUploadPublic = { id, url: vars.url, statusId: 'initiated' };
        this.imageObjects.push({image, sortOrder: idx});
        f.uploadedMeta.id = id;
        console.log({id});
        this.hasUploadedTemp = true;
        this.updateLoading([f.filename], true)
        idx++;
      } catch (err) {
        console.log({err: err});
        f.uploadErrors = "Error uploading";
        this.snack.open('Upload failed', 'Close', { duration: 8000 });
      }
    }

    // Helper: convert any image file to WebP blob
    function convertToWebp(file: Blob | MediaSource, quality = 0.8) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const context = canvas.getContext('2d');
          if(!context) return reject(new Error('Canvas context creation failed'));
          context.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => blob ? resolve(blob) : reject(new Error('Conversion failed')),
            'image/webp',
            quality
          );
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }

    function convertHeicToJpeg(file: Blob) {
      return new Promise(async (resolve, reject) => {
        try {
          const vars: { blob: Blob, type: "bitmap", quality?: undefined } = { blob: file, type: "bitmap" }
          const result = await heicTo(vars);
          resolve(result);
        } catch (err) {
          reject(err);
        }
      });
    }


    this.onImageObjectsUpdated.emit([...this.existingImages, ...this.imageObjects]);
  }

  async removeFiles(indexes: number[]) {
    const removeIds: string[] = []
    indexes.forEach(index=>{
      const f = this.files[index];
      if (f?.uploadedMeta?.key) {
        removeIds.push(f.uploadedMeta.id)
      }
    })
    this.files = this.files.filter((file, idx) => !indexes.includes(idx));  
    this.imageObjects = this.imageObjects.filter((img) => !removeIds.includes(img.image.id));  
    this.onImageObjectsUpdated.emit([...this.existingImages, ...this.imageObjects]);
  }

  async removeImage(id: string){
    this.existingImages = this.existingImages.filter(img=>img.image.id !== id);
    this.imageObjects.splice(this.imageObjects.findIndex(obj=>obj.image.id===id), 1);
    this.onImageObjectsUpdated.emit([...this.existingImages, ...this.imageObjects]);
  }

  private getImageDimensions(file: File): Promise<{ fileSizeInBytes: number, orientation: number, width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = (image) => {
        let orientation = 1;
        try {
          EXIF.getData(img.src,  () => {
              if (file) {
                  orientation = EXIF.getTag(this, 'Orientation') || 1; 
                  var allMetaData = EXIF.pretty(this);
                  console.log(JSON.stringify(allMetaData, null, 2));
              } else {
                  console.error("EXIF data has not been found or incomplete.");
              }
          });
        } catch (error) {
            console.error("Error reading EXIF data", error);
        }
        resolve({ fileSizeInBytes: file.size, orientation, width: img.width, height: img.height })
      };
      img.src = URL.createObjectURL(file);

    });
  }
}
